import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Combobox,
  TextInput,
  useCombobox,
  Group,
  Text,
  Stack,
  Image,
  Box,
  Divider,
  ActionIcon,
  Loader,
  rem,
} from '@mantine/core';
import { useDebouncedValue, useHotkeys } from '@mantine/hooks';
import { IconSearch, IconCategory, IconTag, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getImageSizeUrl } from '@teletienda/shared-ui-lib';

import { useSearchSuggestions } from '@/hooks/useSearchSuggestions';
import { useCurrency } from '@/components/providers/CurrencyProvider';
import { getProductImageUrl } from '@/utils/images';
import { PRODUCT_PLACEHOLDER_SVG } from '@/constants/images';

interface SearchBarProps {
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  width?: string | number;
  maxWidth?: string | number;
}

export function SearchBar({
  placeholder,
  size = 'md',
  width = '100%',
  maxWidth = '500px',
}: SearchBarProps) {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatDualCurrency } = useCurrency();
  const inputRef = useRef<HTMLInputElement>(null);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Initialize query from URL if on search page
  const initialQuery = location.pathname === '/search' ? (searchParams.get('q') || '') : '';
  const [value, setValue] = useState(initialQuery);
  const [debounced] = useDebouncedValue(value, 300);

  const { data, isLoading, isFetching } = useSearchSuggestions(debounced);

  // Focus input on Ctrl+K / Cmd+K
  useHotkeys([['mod+K', () => inputRef.current?.focus()]]);

  // CF-SEARCH-1: intentionally NO URL→input sync effect here. The input mirrors
  // the URL `q` only on first mount (via the useState initializer above) for
  // deep-links/refresh. Mirroring it on every searchParams change would
  // immediately refill the box after handleSearch() clears it, defeating the
  // Storefront-parity "clear on Enter" behaviour.

  // Open dropdown when data arrives
  useEffect(() => {
    if (data && (data.products.length > 0 || data.categories.length > 0 || data.brands.length > 0)) {
      combobox.openDropdown();
    }
  }, [data]);

  const showLoading = isLoading || isFetching;
  const hasResults = data &&
    (data.products.length > 0 || data.categories.length > 0 || data.brands.length > 0);

  const handleSearch = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed) {
      combobox.closeDropdown();
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
      // CF-SEARCH-1: clear the input on submit (Storefront parity) so the
      // query does not linger in the box and the dropdown stays closed.
      setValue('');
    }
  }, [value, navigate, combobox]);

  const handleClear = useCallback(() => {
    setValue('');
    combobox.closeDropdown();
    if (location.pathname === '/search') {
      navigate('/search', { replace: true });
    }
  }, [combobox, location.pathname, navigate]);

  const handleOptionSubmit = useCallback((val: string) => {
    // CF-SEARCH-4 follow-up: every suggestion selection clears the input and
    // keeps the dropdown closed (Storefront SearchAutocomplete parity). Refilling
    // the box with the product/brand name left the query lingering and the
    // suggestions dropdown re-opened on its own.
    if (val.startsWith('product:')) {
      const name = val.replace('product:', '');
      combobox.closeDropdown();
      navigate(`/search?q=${encodeURIComponent(name)}`);
      setValue('');
    } else if (val.startsWith('category:')) {
      const guid = val.replace('category:', '');
      combobox.closeDropdown();
      navigate(`/search?category=${guid}`);
      setValue('');
    } else if (val.startsWith('brand:')) {
      const name = val.replace('brand:', '');
      combobox.closeDropdown();
      navigate(`/search?q=${encodeURIComponent(name)}`);
      setValue('');
    }
  }, [navigate, combobox]);

  return (
    <div style={{ width, maxWidth }}>
      <Combobox
        store={combobox}
        onOptionSubmit={handleOptionSubmit}
        position="bottom-start"
        middlewares={{ flip: false, shift: false }}
        offset={5}
      >
        <Combobox.Target>
          <TextInput
            ref={inputRef}
            value={value}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setValue(v);
              combobox.resetSelectedOption();
              if (v.length >= 2) {
                combobox.openDropdown();
              } else {
                combobox.closeDropdown();
              }
            }}
            onFocus={() => {
              if (hasResults) {
                combobox.openDropdown();
              }
            }}
            onBlur={() => combobox.closeDropdown()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                // If a suggestion is highlighted via arrow keys, let Mantine's
                // built-in Combobox.Target handler call clickSelectedOption()
                if (combobox.dropdownOpened && document.querySelector('[data-combobox-selected]')) {
                  return;
                }
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder={placeholder || t('search_placeholder')}
            size={size}
            leftSection={<IconSearch style={{ width: rem(16), height: rem(16) }} />}
            rightSection={
              <Group gap="xs">
                {showLoading && <Loader size="xs" />}
                {value && (
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleClear}
                    aria-label={t('clear_search')}
                  >
                    <IconX style={{ width: rem(14), height: rem(14) }} />
                  </ActionIcon>
                )}
              </Group>
            }
            styles={{
              input: { borderRadius: rem(30) },
            }}
          />
        </Combobox.Target>

        <Combobox.Dropdown hidden={!hasResults && !showLoading}>
          <Combobox.Options>
            {/* Products */}
            {data?.products && data.products.length > 0 && (
              <>
                <Combobox.Group label={t('search.products')}>
                  {data.products.map((product) => (
                    <Combobox.Option
                      key={product.productGuid}
                      value={`product:${product.name}`}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <Image
                          src={getImageSizeUrl(getProductImageUrl(product.primaryImageUrl), 'thumb')}
                          alt={product.name}
                          w={40}
                          h={40}
                          radius="sm"
                          fallbackSrc={PRODUCT_PLACEHOLDER_SVG}
                        />
                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" lineClamp={1}>{product.name}</Text>
                          <Text size="xs" c="dimmed">
                            {formatDualCurrency(product.price, product.currency)}
                          </Text>
                        </Stack>
                      </Group>
                    </Combobox.Option>
                  ))}
                </Combobox.Group>
                {(data.categories.length > 0 || data.brands.length > 0) && <Divider my="xs" />}
              </>
            )}

            {/* Categories */}
            {data?.categories && data.categories.length > 0 && (
              <>
                <Combobox.Group label={t('search.categories')}>
                  {data.categories.map((cat) => (
                    <Combobox.Option
                      key={cat.categoryGuid}
                      value={`category:${cat.categoryGuid}`}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs">
                          <IconCategory size={16} />
                          <Text size="sm">{cat.name}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {t('search.productsCount', { count: cat.count })}
                        </Text>
                      </Group>
                    </Combobox.Option>
                  ))}
                </Combobox.Group>
                {data.brands.length > 0 && <Divider my="xs" />}
              </>
            )}

            {/* Brands */}
            {data?.brands && data.brands.length > 0 && (
              <Combobox.Group label={t('search.brands')}>
                {data.brands.map((brand) => (
                  <Combobox.Option
                    key={brand.brandGuid}
                    value={`brand:${brand.name}`}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="xs">
                        <IconTag size={16} />
                        <Text size="sm">{brand.name}</Text>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {t('search.productsCount', { count: brand.count })}
                      </Text>
                    </Group>
                  </Combobox.Option>
                ))}
              </Combobox.Group>
            )}

            {/* No results */}
            {!showLoading && debounced.length >= 2 && !hasResults && (
              <Box p="sm">
                <Text size="sm" c="dimmed" ta="center">
                  {t('search.noResults', { query: debounced })}
                </Text>
              </Box>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}
